/**
 * SectionLoading — how a slow section behaves while it waits.
 *
 * The rule this file encodes: a page never shows an unexplained blank area, and
 * it never moves once content lands. Factual sections (what you have, what you
 * owe) render immediately from data the browser already has; the calculated
 * sections reserve their space and fill it with a shape that matches what's
 * coming.
 *
 * Timing, deliberately boring:
 *   0–300 ms   nothing (most refreshes finish inside this, a flashed skeleton
 *              is worse than a still page)
 *   300 ms+    a skeleton shaped like the real component
 *   2 s+       plus a line saying what is being worked out
 *
 * Everything here is `motion-safe:` so reduced-motion users get the same
 * reserved space without the shimmer.
 */
import React, { useEffect, useState } from 'react';

export function useLoadingPhase(active) {
  const [phase, setPhase] = useState('hidden');
  useEffect(() => {
    if (!active) {
      setPhase('hidden');
      return undefined;
    }
    const a = setTimeout(() => setPhase('skeleton'), 300);
    const b = setTimeout(() => setPhase('explained'), 2000);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [active]);
  return phase;
}

export function Bar({ className = '' }) {
  return <div className={`rounded bg-muted/70 motion-safe:animate-pulse ${className}`} />;
}

/**
 * Reserves the final height whatever the phase, so nothing below ever jumps.
 */
export function Reserve({ minHeight, phase, label, message, children, testid, className = '' }) {
  return (
    <div
      className={className}
      style={{ minHeight }}
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      data-testid={testid}
    >
      {phase !== 'hidden' && children}
      {phase === 'explained' && message && (
        <p className="text-xs text-muted-foreground mt-2" data-testid={`${testid}-message`}>{message}</p>
      )}
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function StripSkeleton({ phase, message, testid = 'cash-strip-loading', label = 'Loading your summary' }) {
  return (
    <Reserve minHeight={188} phase={phase} label={label} message={message} testid={testid} className="mb-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-border bg-card rounded-2xl border border-border shadow-sm">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="px-4 py-3.5 sm:px-5">
            <Bar className="h-2 w-24" />
            <Bar className="h-5 w-28 mt-2.5" />
            <Bar className="h-2 w-32 mt-2" />
          </div>
        ))}
      </div>
    </Reserve>
  );
}

export function HeroSkeleton({ phase, message, testid, label, minHeight = 300 }) {
  return (
    <Reserve minHeight={minHeight} phase={phase} label={label} message={message} testid={testid} className="mb-4">
      <div className="rounded-2xl bg-evergreen/90 p-6 sm:p-7 grid gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="h-2 w-28 rounded bg-white/20 motion-safe:animate-pulse" />
          <div className="h-8 w-3/4 rounded bg-white/20 motion-safe:animate-pulse mt-4" />
          <div className="h-3 w-1/2 rounded bg-white/15 motion-safe:animate-pulse mt-3" />
          <div className="h-1.5 w-full rounded bg-white/15 motion-safe:animate-pulse mt-6" />
        </div>
        <div className="rounded-xl bg-white/5 p-4 space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-3 rounded bg-white/15 motion-safe:animate-pulse" />
          ))}
        </div>
      </div>
    </Reserve>
  );
}

export function CardSkeleton({ phase, message, testid, label, minHeight = 168, rows = 4 }) {
  return (
    <Reserve minHeight={minHeight} phase={phase} label={label} message={message} testid={testid} className="mb-4">
      <div className="bg-card rounded-2xl border border-border shadow-sm p-5">
        <Bar className="h-4 w-48" />
        <Bar className="h-3 w-64 mt-3" />
        <div className="mt-4 space-y-2">
          {Array.from({ length: rows }).map((_, i) => (
            <Bar key={i} className="h-2.5 w-full" />
          ))}
        </div>
      </div>
    </Reserve>
  );
}

export function ListSkeleton({ phase, message, testid, label, rows = 2 }) {
  return (
    <Reserve minHeight={rows * 92} phase={phase} label={label} message={message} testid={testid} className="mb-4">
      <div className="bg-card rounded-2xl border border-border shadow-sm divide-y divide-border">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="p-4 flex items-center justify-between gap-3">
            <div className="flex-1">
              <Bar className="h-3 w-40" />
              <Bar className="h-2.5 w-56 mt-2" />
            </div>
            <Bar className="h-8 w-24" />
          </div>
        ))}
      </div>
    </Reserve>
  );
}

/** A section that failed. Quiet, retryable, and never hides the rest of the page. */
export function SectionError({ title, onRetry, testid }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-5 shadow-sm mb-4 text-sm text-muted-foreground"
         data-testid={testid}>
      {title}{' '}
      {onRetry && (
        <button type="button" className="underline underline-offset-2 text-charcoal"
                onClick={onRetry} data-testid={`${testid}-retry`}>
          Try again
        </button>
      )}
    </div>
  );
}

/** "Updating…" — shown when usable cached content is on screen being refreshed. */
export function UpdatingChip({ show, testid = 'updating-chip' }) {
  if (!show) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground align-middle"
          data-testid={testid} aria-live="polite">
      <span className="h-1.5 w-1.5 rounded-full bg-gold motion-safe:animate-pulse" />
      Updating&hellip;
    </span>
  );
}
